import { mdiAccountMultiple, mdiAccountOutline, mdiCogOutline, mdiDnsOutline, mdiPencilOutline } from '@mdi/js'
import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import { subject } from '@casl/ability'
import { PRIVATE } from '@/plugins/acl/const'
import {
  areQueriesEqual,
  didSearchOrFilterValuesChange,
  parseFilterArrayFromQuery,
  parseSearchFromQuery,
  parseTableOptionsFromQuery,
  readListQueryFromSession,
  resetTablePageOnFilterChange,
  shouldResetTablePageAfterFetch,
  serializeFilterArrayToQuery,
  serializeSearchToQuery,
  serializeTableOptionsToQuery,
  shouldRestoreListStateFromUrlQuery,
  writeListQueryToSession,
} from '@/utils/listRouteState'

const LIST_ROUTE_NAME = 'apps-employee-list'

export default function useEmployeeList() {
  const vm = getCurrentInstance().proxy
  const employees = ref([])
  const choices = ref({})

  const tagsFilter = ref([])
  const statusesFilter = ref([])
  const languagesFilter = ref([])

  const tableColumns = [
    { text: 'user', value: 'fullName', class: 'ps-7 text-uppercase' },
    { text: 'status', value: 'status', class: 'text-uppercase' },
    { text: 'tags', value: 'tags', sortable: false, class: 'text-uppercase' },
    { text: 'phoneNumber', value: 'phoneNumber', class: 'text-uppercase' },
    {
      text: 'actions',
      value: 'actions',
      align: 'center',
      sortable: false,
      class: 'text-uppercase',
    },
  ]

  const searchQuery = ref('')
  const totalUserListTable = ref(0)
  const loading = ref(false)
  const options = ref({
    sortDesc: [true],
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  })
  const userTotalLocal = ref([])
  const overview = ref([])
  const isSyncingFromRoute = ref(false)

  const { getEmployees, getEmployeeChoices, getEmployeesOverview } = useActions('staff', [
    'getEmployees',
    'getEmployeeChoices',
    'getEmployeesOverview',
  ])

  const fetchEmployeesOverview = async () => {
    overview.value = await getEmployeesOverview()
  }

  const applyStateFromRoute = () => {
    isSyncingFromRoute.value = true

    let query = { ...vm.$route.query }
    if (shouldRestoreListStateFromUrlQuery(query)) {
      const stored = readListQueryFromSession(LIST_ROUTE_NAME)
      if (stored) {
        query = { ...stored }
      }
    }

    tagsFilter.value = parseFilterArrayFromQuery(query, 'tags')
    statusesFilter.value = parseFilterArrayFromQuery(query, 'status')
    languagesFilter.value = parseFilterArrayFromQuery(query, 'languages')
    searchQuery.value = parseSearchFromQuery(query)
    options.value = parseTableOptionsFromQuery(query, options.value)

    isSyncingFromRoute.value = false
    void syncStateToRoute()
  }

  const syncStateToRoute = async () => {
    if (isSyncingFromRoute.value) return

    const nextQuery = {
      ...serializeTableOptionsToQuery(options.value),
      ...serializeFilterArrayToQuery('tags', tagsFilter.value),
      ...serializeFilterArrayToQuery('status', statusesFilter.value),
      ...serializeFilterArrayToQuery('languages', languagesFilter.value),
      ...serializeSearchToQuery(searchQuery.value),
    }

    if (!areQueriesEqual(vm.$route.query, nextQuery)) {
      await vm.$router.replace({ query: nextQuery })
    }
    writeListQueryToSession(LIST_ROUTE_NAME, nextQuery)
  }

  // fetch data
  const fetchEmployees = async (isRetry = false) => {
    loading.value = true
    try {
      const { data, success } = await getEmployees({
        tags: tagsFilter.value,
        status: statusesFilter.value,
        languages: languagesFilter.value,
        search: searchQuery.value,
        page: options.value.page,
        pageSize: options.value.itemsPerPage,
        ordering: options.value.sortBy.map((el, index) => {
          const parsedEl = el
            .replaceAll('.', '__')
            .replace('fullName', 'candidate__user__last_name')
            .replace('phoneNumber', 'candidate__user__phone')

          return `${options.value.sortDesc[index] ? '-' : ''}${parsedEl}`
        }),
      })
      let total = 0
      if (success) {
        if (Object.hasOwnProperty.bind(data)('results') && Object.hasOwnProperty.bind(data)('count')) {
          employees.value = data.results.map((employee) => subject(PRIVATE.STAFF.EMPLOYEES, employee))
          totalUserListTable.value = data.count
          total = data.count
        } else {
          employees.value = data.map((employee) => subject(PRIVATE.STAFF.EMPLOYEES, employee))
          totalUserListTable.value = data.length
          total = data.length
        }
      } else {
        employees.value = []
        totalUserListTable.value = 0
      }
      if (
        !isRetry &&
        shouldResetTablePageAfterFetch({
          success,
          page: options.value.page,
          total,
          pageSize: options.value.itemsPerPage,
        })
      ) {
        options.value = { ...options.value, page: 1 }
        await syncStateToRoute()
        await fetchEmployees(true)
        return
      }
      choices.value = await getEmployeeChoices({
        tags: tagsFilter.value,
        status: statusesFilter.value,
        languages: languagesFilter.value,
        search: searchQuery.value,
      })
    } finally {
      loading.value = false
    }
  }

  const timer = ref(null)
  const delaySearchHandler = async () => {
    clearTimeout(timer.value)
    timer.value = setTimeout(async () => {
      if (options.value.page !== 1) {
        options.value = { ...options.value, page: 1 }
      }
      await syncStateToRoute()
      await fetchEmployees()
    }, 1000)
  }

  applyStateFromRoute()

  watch(
    [tagsFilter, statusesFilter, languagesFilter, options],
    async ([tags, statuses, languages], oldValues) => {
      const filterChanged = didSearchOrFilterValuesChange({
        filterArrays: [tags, statuses, languages],
        oldFilterArrays: oldValues ? oldValues.slice(0, 3) : [],
      })
      if (resetTablePageOnFilterChange(options, filterChanged)) {
        await syncStateToRoute()
        return
      }
      await syncStateToRoute()
      await fetchEmployees()
    },
  )

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserTotalIcon = (title) => {
    if (title === 'total') return { icon: mdiAccountMultiple, color: 'secondary' }
    if (title === 'assigned') return { icon: mdiDnsOutline, color: 'primary' }
    if (title === 'files') return { icon: mdiCogOutline, color: 'success' }

    return { icon: mdiAccountOutline, color: 'primary' }
  }

  return {
    fetchEmployeesOverview,
    fetchEmployees,
    resolveUserTotalIcon,
    delaySearchHandler,

    tableColumns,
    searchQuery,
    totalUserListTable,
    loading,
    options,
    userTotalLocal,
    overview,
    employees,
    choices,
    tagsFilter,
    statusesFilter,
    languagesFilter,
  }
}
